// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductBuyButton from "@awc/App/Components/Product/ProductBuyButton";
import ProductLeadTime from "@awc/App/Components/Product/ProductLeadTime";
import ProductQuoteButton from "@awc/App/Components/Product/ProductQuoteButton";
import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // XNG-Changes
    showCompare?: boolean;
    showAvailability?: boolean;
    showUnitOfMeasure?: boolean;
    includeBreakPriceRange?: boolean;
    isTableView?: boolean;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // XNG-Changes
    actionButtons?: InjectableCss;
    productListCompareAddToListWrapper?: ProductListProductImageStyles;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    // XNG-Change
    showCompare,
    showAvailability,
    showUnitOfMeasure,
    includeBreakPriceRange,
    isTableView,
    isLoading,
}) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice &&
                (isLoading ? (
                    <Skeleton height="18px" />
                ) : (
                    <>
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.price}
                            // XNG-Changes
                            showUnitOfMeasure={showUnitOfMeasure}
                            {...{ includeBreakPriceRange }}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </>
                ))}
            {/* XNG-Changes */}
            {showAvailability && (isLoading ? <Skeleton height="14px" /> : <ProductLeadTime />)}
            <StyledWrapper {...styles.actionButtons}>
                {!isLoading ? (
                    <>
                        {/* XNG-Changes On PLP, display buy now button if RT complete */}
                        {productContext.productInfo.pricing && (
                            <ProductBuyButton isPlp={true} isTableView={isTableView} />
                        )}
                        <ProductQuoteButton />
                    </>
                ) : (
                    <Skeleton height="14px" />
                )}
            </StyledWrapper>
            {(showCompare || showAddToList) &&
                (!isLoading ? (
                    <ProductListProductImage
                        showImage={false}
                        showCompare={!!showCompare}
                        {...{ showAddToList }}
                        extendedStyles={styles.productListCompareAddToListWrapper}
                    />
                ) : (
                    <Skeleton height="14px" />
                ))}
            {/* <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper> */}
            {/* {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )} */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
